import cn from 'classnames'
import { ReactNode } from 'react'
import { parseError } from 'common/utils/parseError'
import {
  CheckCircleIcon,
  CircleXIcon,
  InfoIcon,
  WarningIcon,
} from 'components/icons'

interface Messages {
  /** The error message to be displayed, replaces \n */
  children?: ReactNode
  className?: string
  icon?: string | ReactNode
  'data-test'?: string
}

//Generic error message
const Message = ({
  children,
  className,
  'data-test': dataTest,
  icon,
}: Messages) => {
  if (!children) {
    return null
  }

  return (
    <>
      <div
        data-test={dataTest}
        className={`p-4 fw-semibold rounded border border-1 ${className || ''}`}
      >
        <div className='flex-row align-items-center'>
          {icon && typeof icon === 'string' ? (
            <span className={cn({ icon: true }, 'me-3', icon)} />
          ) : (
            icon
          )}
          {typeof children === 'string' ? (
            <span
              style={{
                overflowWrap: 'anywhere',
              }}
              dangerouslySetInnerHTML={{
                __html: children.replace(/\n/g, ''),
              }}
              data-test='message'
            ></span>
          ) : (
            children
          )}
        </div>
      </div>
    </>
  )
}

//Default message added alert-danger
export const ErrorMessage = ({ className, ...props }: Messages) =>
  !props.children ? null : (
    <Message
      {...props}
      icon={
        props.icon || <CircleXIcon className='me-2' width={28} height={28} />
      }
      className={cn(
        className,
        'bg-danger text-danger bg-opacity-10 border-danger',
      )}
    >
      {parseError(props.children)}
    </Message>
  )

// Default message added alert-success
export const SuccessMessage = ({ className, ...props }: Messages) =>
  !props.children ? null : (
    <Message
      {...props}
      icon={
        props.icon || (
          <CheckCircleIcon
            className='me-2'
            width={28}
            height={28}
            fill='#4CB67E'
          />
        )
      }
      className={cn(className, 'bg-success border-success bg-opacity-10')}
    />
  )

export const InfoMessage = ({ className, ...props }: Messages) =>
  !props.children ? null : (
    <Message
      {...props}
      icon={props.icon || <InfoIcon className='me-2' width={28} height={28} />}
      className={cn(className, 'bg-action100 border-action200')}
    />
  )

export const WarningMessage = ({ className, ...props }: Messages) =>
  !props.children ? null : (
    <Message
      {...props}
      icon={
        props.icon || <WarningIcon className='me-2' width={28} height={28} />
      }
      className={cn(className, 'bg-warning border-warning')}
    />
  )

Message.displayName = 'ErrorMessage'
